import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { formatDateRange } from "@/core/utils/date";
import type { IPayrollSheetApprovalStatus } from "@/payroll/payroll.d";
import { RequestApprovalRecipientRow } from "./request-approval-recipient-row";
import type { ISheetApprovalRequestState } from "./types";
import {
  getSelectableRecipients,
  hasAnyApproval,
  resolveUnitApprovals,
} from "./utils";

interface RequestApprovalDialogBodyProps {
  state: ISheetApprovalRequestState;
  approvalStatus: IPayrollSheetApprovalStatus | null | undefined;
  selectedIds: string[];
  onToggle: (userId: string) => void;
  onToggleAll: () => void;
}

/** Recipient selection and approval context inside the request dialog. */
export const RequestApprovalDialogBody = ({
  state,
  approvalStatus,
  selectedIds,
  onToggle,
  onToggleAll,
}: RequestApprovalDialogBodyProps) => {
  const selectable = getSelectableRecipients(state);
  const units = resolveUnitApprovals(state.approval_units, approvalStatus);
  const showStatus = hasAnyApproval(units);
  const allSelected = selectedIds.length === selectable.length;

  return (
    <Stack
      align="start"
      className="min-h-0 w-full overflow-hidden pb-2"
      gap={4}
    >
      <Text tone="muted">
        Each person you pick gets one email for{" "}
        <strong className="font-semibold">
          {formatDateRange(state.period.start_date, state.period.end_date)}
        </strong>
        , covering only their own sheets, with a link to sign in and approve.
      </Text>

      <Stack
        align="start"
        gap={0}
        className="min-h-0 w-full flex-1 overflow-hidden rounded-lg border border-line"
      >
        <Stack
          direction="row"
          align="center"
          justify="between"
          gap={2}
          className="w-full shrink-0 border-b border-line bg-surface-sunken px-4 py-2"
        >
          <Text variant="caption" tone="muted">
            {selectedIds.length} of {selectable.length} selected
          </Text>
          <Button
            variant="link"
            size="sm"
            onClick={onToggleAll}
            className="h-auto px-0 py-0 font-medium"
          >
            {allSelected ? "Clear all" : "Select all"}
          </Button>
        </Stack>

        <div className="min-h-0 w-full flex-1 overflow-y-auto">
          <Stack align="start" gap={0} className="w-full divide-y divide-line">
            {selectable.map((recipient) => (
              <RequestApprovalRecipientRow
                key={recipient.user_id}
                recipient={recipient}
                units={units}
                showStatus={showStatus}
                checked={selectedIds.includes(recipient.user_id)}
                onToggle={onToggle}
              />
            ))}
          </Stack>
        </div>
      </Stack>
    </Stack>
  );
};
